import { GridActionsCellItem } from '@mui/x-data-grid';
import { Paper, IconButton, Grid, Tooltip } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility,
} from '@mui/icons-material';
import { GridRowParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import useConfirmDeleteDialog from '../../hooks/useConfirmDeleteDialog';
import useCheckPermission from '../../hooks/useCheckPermission';
import licenseService from './licenseService';
import DataList from '../../components/DataList';

const LicensesPage = () => {
  const navigate = useNavigate();
  const { ConfirmDeleteDialog, setParams } = useConfirmDeleteDialog();
  const [rows, setRows] = useState<any[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();
  const { canPlatform } = useCheckPermission();

  useEffect(() => {
    dispatch({ type: 'SHOW_QUERY' });
    licenseService
      .list()
      .then((response) => {
        if (!response.hasError) setRows(response.records);
        else
          enqueueSnackbar(response.errorMessages.join(','), {
            variant: 'error',
          });
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  }, []);

  const getActions = (params: GridRowParams) => {
    const actions: any[] = [];
    const readonly = !canPlatform(['ManageLicenses']);
    actions.push(
      <GridActionsCellItem
        color="primary"
        icon={
          readonly ? (
            <Tooltip title="View">
              <Visibility />
            </Tooltip>
          ) : (
            <Tooltip title="Edit">
              <EditIcon />
            </Tooltip>
          )
        }
        label={readonly ? 'View' : 'Edit'}
        onClick={() => navigate(`/licenses/edit/${params.row.id}`)}
      />
    );

    if (canPlatform(['ManageLicenses'])) {
      actions.push(
        <GridActionsCellItem
          color="primary"
          icon={
            <Tooltip title="Delete">
              <DeleteIcon />
            </Tooltip>
          }
          label="Delete"
          onClick={() =>
            setParams({
              open: true,
              name: 'Are you sure to delete this item?',
              onConfirm: async () => handleDelete(params.row.id),
            })
          }
        />
      );
    }

    return actions;
  };

  const columns: any[] = [
    { field: 'name', headerName: 'Name', flex: 100 },
    { field: 'description', headerName: 'Description', flex: 120 },
    { field: 'maxDevices', headerName: 'Max Devices', flex: 60 },
    { field: 'includedModuleCodes', headerName: 'Modules', flex: 120 },
    {
      field: 'actions',
      type: 'actions',
      flex: 30,
      getActions,
    },
  ];

  const handleDelete = (id: string) => {
    licenseService.delete(id).then((response) => {
      if (response.hasError) {
        enqueueSnackbar((response.errorMessages ?? []).join(','), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Successful delete', { variant: 'success' });
        setRows((prev) => prev.filter((row) => row.id !== id));
      }
    });
  };

  return (
    <Paper>
      <Grid container sx={{ p: 3 }}>
        <Grid size={12}>
          <h2>Licenses</h2>
        </Grid>
        {canPlatform(['ManageLicenses']) && (
          <Grid container size={12} sx={{ p: 2, justifyContent: 'end' }}>
            <Grid>
              <Tooltip title="Create License">
                <IconButton component={RouterLink} to={`/licenses/create`} color="primary">
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        )}
        <Grid size={12}>
          <DataList rows={rows} columns={columns} />
        </Grid>
        <ConfirmDeleteDialog />
      </Grid>
    </Paper>
  );
};

export default LicensesPage;
